/* ==========================================================================
   RevTap — theme.js
   Vanilla JS, pas de dépendances. Custom elements pour chaque composant.
   ========================================================================== */
(() => {
  'use strict';

  const RT = window.RevTap || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Utils ---------- */
  const formatMoney = (cents, format = RT.moneyFormat || '{{amount_with_comma_separator}} €') => {
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const intSpaced = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    let out;
    if (format.includes('amount_no_decimals')) out = intSpaced;
    else if (format.includes('amount_with_comma_separator')) out = `${intSpaced},${dec}`;
    else out = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, out);
  };

  const trapFocus = (container) => {
    const focusables = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusables.length) return () => {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  const toast = (message, type = 'success') => {
    const region = $('#ToastRegion');
    if (!region) return;
    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.textContent = message;
    region.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => el.remove(), 300);
    }, 2800);
  };

  const publish = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ---------- Drawer (menu, panier, filtres) ---------- */
  class RTDrawer extends HTMLElement {
    constructor() {
      super();
      this.panel = $('.drawer__panel', this);
      this.overlay = $('.drawer__overlay', this);
      this.releaseFocus = null;
      this.lastActive = null;
    }
    connectedCallback() {
      // Délégation : le contenu du tiroir panier est remplacé à chaque mise à jour AJAX,
      // les boutons de fermeture doivent donc rester fonctionnels sans ré-attacher d'écouteur.
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.overlay) this.close();
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      $$(`[data-drawer-open="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
    }
    get isOpen() { return this.classList.contains('is-open'); }
    open() {
      this.lastActive = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      this.panel = $('.drawer__panel', this);
      this.releaseFocus = trapFocus(this.panel);
      setTimeout(() => ($('[data-drawer-close]', this) || this.panel).focus(), 60);
      publish('rt:drawer:open', { id: this.id });
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      this.releaseFocus?.();
      this.lastActive?.focus?.();
      publish('rt:drawer:close', { id: this.id });
    }
  }
  customElements.define('rt-drawer', RTDrawer);

  /* ---------- Modal ---------- */
  class RTModal extends HTMLElement {
    connectedCallback() {
      $$('[data-modal-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      $('.modal__overlay', this)?.addEventListener('click', () => this.close());
      $$(`[data-modal-open="${this.id}"]`).forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() { this.classList.add('is-open'); document.body.classList.add('is-locked'); }
    close() { this.classList.remove('is-open'); document.body.classList.remove('is-locked'); }
  }
  customElements.define('rt-modal', RTModal);

  /* ---------- Liens d'ancre (#section) : défilement piloté en JS ----------
     Le défilement doux natif peut s'arrêter en route sur mobile (barre du navigateur qui se replie,
     contenus qui se chargent et décalent la page). Ici la position de la cible est recalculée à
     chaque image : un seul tap suffit, et on arrive toujours au bon endroit. */
  let anchorRaf = 0;
  const stopAnchorScroll = () => cancelAnimationFrame(anchorRaf);
  ['touchstart', 'wheel', 'keydown'].forEach((type) => window.addEventListener(type, stopAnchorScroll, { passive: true }));
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('a[href*="#"]');
    if (!link || link.target === '_blank' || link.hasAttribute('onclick') || link.classList.contains('skip-link')) return;
    const url = new URL(link.href, window.location.href);
    if (url.hash.length < 2 || url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target || !target.getClientRects().length) return; // cible absente ou masquée : comportement natif
    e.preventDefault();
    stopAnchorScroll();
    if (url.hash !== window.location.hash) history.pushState(null, '', url.hash);
    const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    const destination = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return Math.max(0, Math.min(target.getBoundingClientRect().top + window.scrollY - offset, max));
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top: destination(), behavior: 'instant' });
      return;
    }
    const from = window.scrollY;
    const duration = Math.min(900, Math.max(350, Math.abs(destination() - from) * 0.5));
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      window.scrollTo({ top: from + (destination() - from) * eased, behavior: 'instant' });
      if (t < 1) anchorRaf = requestAnimationFrame(step);
    };
    anchorRaf = requestAnimationFrame(step);
  });

  /* ---------- Header : compact au scroll ---------- */
  class RTHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      const onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 8);
        // Masqué en descendant, réaffiché dès qu'on remonte (petits mouvements ignorés pour éviter le clignotement)
        if (this.dataset.hideOnScroll === 'true' && (Math.abs(y - this.lastY) > 6 || y <= 160)) {
          this.classList.toggle('is-hidden', y > this.lastY && y > 160);
          this.lastY = y;
        } else if (this.dataset.hideOnScroll !== 'true') {
          this.lastY = y;
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      // Expose la hauteur réelle du header pour le sticky ATC etc.
      const setHeight = () => document.documentElement.style.setProperty('--header-real-height', `${this.offsetHeight}px`);
      setHeight();
      window.addEventListener('resize', setHeight);
    }
  }
  customElements.define('rt-header', RTHeader);

  /* ---------- Cart API ---------- */
  const Cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || RT.strings?.cartError);
      return data;
    },
    async add(items) {
      const data = await this.request(RT.routes.cartAdd, { items, sections: this.sections() });
      await this.refresh(data.sections);
      return data;
    },
    async change(line, quantity) {
      const data = await this.request(RT.routes.cartChange, { line, quantity, sections: this.sections() });
      await this.refresh(data.sections);
      return data;
    },
    async changeByKey(id, quantity) {
      const data = await this.request(RT.routes.cartChange, { id, quantity, sections: this.sections() });
      await this.refresh(data.sections);
      return data;
    },
    sections() {
      return ['cart-drawer', 'cart-icon-bubble'].join(',');
    },
    async refresh(sections) {
      if (!sections) {
        const res = await fetch(`${RT.routes.cart}?sections=${this.sections()}`);
        sections = await res.json();
      }
      if (sections['cart-drawer']) {
        const drawer = $('#CartDrawer');
        if (drawer) {
          const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const fresh = $('#CartDrawer .drawer__panel', html);
          if (fresh) $('.drawer__panel', drawer).replaceWith(fresh);
          drawer.classList.toggle('is-empty', html.querySelector('#CartDrawer')?.classList.contains('is-empty'));
        }
      }
      if (sections['cart-icon-bubble']) {
        const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const bubble = $('#cart-icon-bubble', html);
        $$('[data-cart-bubble]').forEach((el) => { el.innerHTML = bubble ? bubble.innerHTML : ''; });
      }
      publish('rt:cart:updated');
    }
  };
  window.RevTap.Cart = Cart;

  /* ---------- Cart drawer : quantités, suppression ---------- */
  class RTCartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-cart-remove], [data-qty-change]');
        if (!btn) return;
        e.preventDefault();
        const line = btn.closest('[data-line]');
        const key = line?.dataset.line;
        const input = $('[data-qty-input]', line);
        let qty = 0;
        if (btn.dataset.qtyChange) qty = Math.max(0, parseInt(input.value, 10) + parseInt(btn.dataset.qtyChange, 10));
        line?.classList.add('is-loading');
        try { await Cart.changeByKey(key, qty); } catch (err) { toast(err.message, 'error'); line?.classList.remove('is-loading'); }
      });
      this.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (!input) return;
        const line = input.closest('[data-line]');
        try { await Cart.changeByKey(line.dataset.line, Math.max(0, parseInt(input.value, 10) || 0)); } catch (err) { toast(err.message, 'error'); }
      });
    }
  }
  customElements.define('rt-cart-drawer', RTCartDrawer);

  /* ---------- Product form (ajout panier) ---------- */
  class RTProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.submit = $('[type="submit"]', this.form);
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.submit?.disabled) return;
      const fd = new FormData(this.form);
      const id = fd.get('id');
      const quantity = parseInt(fd.get('quantity') || '1', 10);
      const properties = {};
      fd.forEach((v, k) => { const m = k.match(/^properties\[(.+)\]$/); if (m && v) properties[m[1]] = v; });
      this.submit?.classList.add('is-loading');
      try {
        await Cart.add([{ id, quantity, properties }]);
        if (document.body.dataset.cartType === 'page' || !$('#CartDrawer')) {
          window.location.href = RT.routes.cart;
          return;
        }
        $('#CartDrawer')?.open();
      } catch (err) {
        toast(err.message || RT.strings?.cartError, 'error');
      } finally {
        this.submit?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('rt-product-form', RTProductForm);

  /* ---------- Barre d'ajout au panier fixe (mobile) : visible quand le bouton principal est hors écran ---------- */
  class RTStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.anchor = this.form && $('.product__buy', this.form);
      if (!this.form || !this.anchor) return;
      $('[data-sticky-submit]', this)?.addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit(); else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
      // Prix et état du bouton : miroir du bloc prix / bouton principal
      const price = document.getElementById(this.dataset.price);
      const mainBtn = $('[type="submit"]', this.form);
      const sync = () => {
        const reg = price && $('.price__regular', price); const mine = $('[data-sticky-price] .price__regular', this);
        if (reg && mine) mine.textContent = reg.textContent;
        const btn = $('[data-sticky-submit]', this); const txt = mainBtn && $('[data-btn-text]', mainBtn);
        if (btn && mainBtn) { btn.disabled = mainBtn.disabled; const t = $('[data-btn-text]', btn); if (t && txt) t.textContent = txt.textContent; }
      };
      sync();
      if (price) new MutationObserver(sync).observe(price, { childList: true, subtree: true, characterData: true });
      if (mainBtn) new MutationObserver(sync).observe(mainBtn, { attributes: true, childList: true, subtree: true, characterData: true });
      // Affichage : seulement quand le bouton principal est passé au-dessus de l'écran.
      // (Écouteur scroll plutôt qu'IntersectionObserver : un saut de page qui "enjambe"
      // l'élément sans le croiser ne déclenche pas l'observer.)
      let pending = null;
      const update = () => {
        pending = null;
        const above = this.anchor.getBoundingClientRect().bottom < 0;
        if (above === this.shown) return;
        this.shown = above;
        if (above) { this.hidden = false; setTimeout(() => this.classList.add('is-visible'), 20); }
        else { this.classList.remove('is-visible'); setTimeout(() => { if (!this.shown) this.hidden = true; }, 350); }
        document.body.classList.toggle('has-sticky-atc', above);
      };
      const onScroll = () => { if (!pending) pending = setTimeout(update, 60); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    }
  }
  customElements.define('rt-sticky-atc', RTStickyAtc);

  /* ---------- Quick add (carte produit, 1 variante ou variante par défaut) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.quickAddUrl) { window.location.href = btn.dataset.quickAddUrl; return; }
    btn.classList.add('is-loading');
    try {
      await Cart.add([{ id: btn.dataset.quickAdd, quantity: 1 }]);
      $('#CartDrawer') ? $('#CartDrawer').open() : toast(RT.strings?.added);
    } catch (err) { toast(err.message, 'error'); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Quantité (+/-) générique ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.quantity__btn[data-step]');
    if (!btn || btn.closest('rt-cart-drawer')) return;
    const input = $('.quantity__input', btn.closest('.quantity'));
    const min = parseInt(input.min || '1', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + parseInt(btn.dataset.step, 10)));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Sélecteur de variantes ---------- */
  class RTVariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[type="application/json"]', this)?.textContent || '[]');
      this.addEventListener('change', () => this.onChange());
    }
    get selectedOptions() {
      return $$('fieldset', this).map((fs) => $('input:checked', fs)?.value ?? $('select', fs)?.value);
    }
    onChange() {
      const opts = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const form = document.getElementById(this.dataset.form);
      const idInput = form && $('input[name="id"]', form);
      const btn = form && $('[type="submit"]', form);
      const btnText = btn && $('[data-btn-text]', btn);
      const priceEl = document.getElementById(this.dataset.price);
      if (!variant) {
        if (btn) { btn.disabled = true; if (btnText) btnText.textContent = RT.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (btn) { btn.disabled = !variant.available; if (btnText) btnText.textContent = variant.available ? RT.strings.addToCart : RT.strings.soldOut; }
      if (priceEl) {
        priceEl.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        const reg = $('.price__regular', priceEl); if (reg) reg.textContent = formatMoney(variant.price);
        const cmp = $('.price__compare', priceEl);
        if (cmp) { cmp.textContent = formatMoney(variant.compare_at_price || 0); cmp.hidden = !(variant.compare_at_price > variant.price); }
      }
      if (variant.featured_media && this.dataset.gallery) {
        const target = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      publish('rt:variant:change', { variant, form: this.dataset.form });
    }
  }
  customElements.define('rt-variant-picker', RTVariantPicker);

  /* ---------- Paliers de quantité (packs) ---------- */
  class RTQuantityTiers extends HTMLElement {
    connectedCallback() {
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;      // prix unitaire de la variante
      this.comparePrice = parseInt(this.dataset.comparePrice, 10) || this.basePrice; // prix barré unitaire
      this.addEventListener('change', (e) => { if (e.target.matches('.tier__input')) this.select(e.target); });
      document.addEventListener('rt:variant:change', (e) => {
        if (e.detail.form !== this.dataset.form) return;
        this.basePrice = e.detail.variant.price;
        this.comparePrice = Math.max(e.detail.variant.compare_at_price || 0, this.basePrice);
        this.renderPrices();
        const checked = $('.tier__input:checked', this);
        if (checked) this.updateMainPrice(checked);
      });
      const checked = $('.tier__input:checked', this);
      if (checked) this.select(checked);
    }
    get form() { return document.getElementById(this.dataset.form); }
    totalFor(input) {
      const qty = parseInt(input.dataset.qty, 10);
      const fixed = parseInt(input.dataset.total, 10);
      return { qty, total: fixed > 0 ? fixed : this.basePrice * qty, full: this.comparePrice * qty };
    }
    select(input) {
      $$('[data-tier]', this).forEach((t) => t.classList.toggle('is-selected', t.contains(input)));
      const form = this.form;
      const qtyInput = form && $('[name="quantity"]', form);
      if (qtyInput) {
        qtyInput.value = input.dataset.qty;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updateMainPrice(input);
    }
    /* Le bloc prix principal reflète le pack sélectionné */
    updateMainPrice(input) {
      const priceEl = document.getElementById(this.dataset.price);
      if (!priceEl) return;
      const { total, full } = this.totalFor(input);
      const reg = $('.price__regular', priceEl); if (reg) reg.textContent = formatMoney(total);
      const cmp = $('.price__compare', priceEl);
      if (cmp) { cmp.textContent = formatMoney(full); cmp.hidden = !(full > total); }
      priceEl.classList.toggle('price--on-sale', full > total);
    }
    renderPrices() {
      $$('[data-tier]', this).forEach((tier) => {
        const input = $('.tier__input', tier);
        const { total, full } = this.totalFor(input);
        const set = (sel, txt) => { const el = $(sel, tier); if (el) el.textContent = txt; };
        set('[data-tier-total]', formatMoney(total));
        set('[data-tier-full]', formatMoney(full));
        const pct = full > total ? Math.round(((full - total) / full) * 100) : 0;
        set('[data-tier-pct]', `-${pct}%`);
        const sav = $('[data-tier-savings]', tier);
        if (sav) { sav.hidden = full <= total; sav.textContent = (RT.strings.youSave || '[amount]').replace('[amount]', formatMoney(full - total)); }
      });
    }
  }
  customElements.define('rt-quantity-tiers', RTQuantityTiers);

  /* ---------- Date de livraison estimée (J+N, dimanches non comptés) ---------- */
  class RTDeliveryDate extends HTMLElement {
    connectedCallback() {
      const days = parseInt(this.dataset.days, 10) || 3;
      const skipSunday = this.dataset.skipSunday === 'true';
      const cutoff = parseInt(this.dataset.cutoff, 10) || 0;
      const el = $('[data-delivery-text]', this);
      // Date/heure de Paris
      const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hour12: false }).formatToParts(new Date());
      const g = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
      const d = new Date(Date.UTC(g('year'), g('month') - 1, g('day')));
      if (cutoff && g('hour') >= cutoff) d.setUTCDate(d.getUTCDate() + 1);
      let remaining = days;
      while (remaining > 0) {
        d.setUTCDate(d.getUTCDate() + 1);
        if (skipSunday && d.getUTCDay() === 0) continue;
        remaining -= 1;
      }
      if (skipSunday && d.getUTCDay() === 0) d.setUTCDate(d.getUTCDate() + 1);
      const label = new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(d);
      const pretty = label.charAt(0).toUpperCase() + label.slice(1);
      if (el) el.textContent = (this.dataset.text || '[date]').replace('[date]', pretty);
    }
  }
  customElements.define('rt-delivery-date', RTDeliveryDate);

  /* ---------- Étapes de livraison : compte à rebours → livraison (heure de Paris) ---------- */
  class RTShippingSteps extends HTMLElement {
    connectedCallback() {
      this.cutoff = parseInt(this.dataset.cutoff, 10) || 24;
      this.weekdaysOnly = this.dataset.weekdaysOnly === 'true';
      this.days = parseInt(this.dataset.days, 10) || 3;
      this.skipSunday = this.dataset.skipSunday === 'true';
      this.orderEl = $('[data-step-order]', this);
      this.shipEl = $('[data-step-ship]', this);
      this.deliverEl = $('[data-step-deliver]', this);
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    /* Texte au-dessus, badge toujours seul sur sa ligne (« | » = retour à la ligne supplémentaire).
       Le badge est créé une fois, puis seul son texte est mis à jour. */
    renderBadge(el, tpl, placeholder, value) {
      const badge = $('.ship-steps__badge', el);
      if (badge) { if (badge.textContent !== value) badge.textContent = value; return; }
      const frag = document.createDocumentFragment();
      const addLine = (txt) => {
        if (!txt) return;
        const row = document.createElement('span');
        row.className = 'ship-steps__line';
        row.textContent = txt;
        frag.append(row);
      };
      const [before, after] = tpl.split(placeholder);
      before.split('|').map((l) => l.trim()).forEach(addLine);
      const b = document.createElement('span');
      b.className = 'badge badge--accent ship-steps__badge';
      b.textContent = value;
      frag.append(b);
      (after || '').split('|').map((l) => l.trim()).forEach(addLine);
      el.replaceChildren(frag);
    }
    parisNow() {
      const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false }).formatToParts(new Date());
      const g = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
      return { y: g('year'), mo: g('month'), d: g('day'), h: g('hour') % 24, m: g('minute'), s: g('second') };
    }
    tick() {
      const now = this.parisNow();
      const today = new Date(Date.UTC(now.y, now.mo - 1, now.d));

      /* 1. Compte à rebours jusqu'à l'heure limite */
      const secondsLeft = Math.max(0, this.cutoff * 3600 - (now.h * 3600 + now.m * 60 + now.s));
      const hh = Math.floor(secondsLeft / 3600);
      const mm = Math.floor((secondsLeft % 3600) / 60);
      const ss = secondsLeft % 60;
      const timer = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`; // même format que la barre d'annonce (HH:MM:SS)
      if (this.orderEl) this.renderBadge(this.orderEl, this.dataset.textOrder || '[timer]', '[timer]', timer);

      /* 2. Jour d'expédition : demain, ou lundi le week-end / vendredi après l'heure limite */
      const days = RT.strings?.days || { tomorrow: 'demain', monday: 'lundi' };
      const wd = today.getUTCDay(); // 0 = dimanche, 6 = samedi
      let shipDay = days.tomorrow;
      if (this.weekdaysOnly && (wd === 6 || wd === 0 || (wd === 5 && secondsLeft <= 0))) shipDay = days.monday;
      if (this.shipEl) this.renderBadge(this.shipEl, this.dataset.textShip || '[day]', '[day]', shipDay.charAt(0).toUpperCase() + shipDay.slice(1));

      /* 3. Date de livraison : J+N à partir d'aujourd'hui, dimanches non comptés */
      const dl = new Date(today);
      if (secondsLeft <= 0) dl.setUTCDate(dl.getUTCDate() + 1);
      let remaining = this.days;
      while (remaining > 0) {
        dl.setUTCDate(dl.getUTCDate() + 1);
        if (this.skipSunday && dl.getUTCDay() === 0) continue;
        remaining -= 1;
      }
      const label = new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(dl);
      // « lundi 5 octobre » → « Lundi 5 Octobre »
      const pretty = label.replace(/(^|\s)(\p{L})/gu, (m, sp, c) => sp + c.toUpperCase());
      if (this.deliverEl) this.renderBadge(this.deliverEl, this.dataset.textDeliver || '[date]', '[date]', pretty);
    }
  }
  customElements.define('rt-shipping-steps', RTShippingSteps);

  /* ---------- Configuration de la plaque (commerce Google / pseudo / lien) ---------- */
  class RTPlatformSetup extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-setup-input]', this);
      this.error = $('[data-setup-error]', this);
      this.status = $('[data-setup-status]', this);
      this.suggestions = $('[data-setup-suggestions]', this);
      this.placeId = $('[data-setup-place-id]', this);
      this.reviewLink = $('[data-setup-review-link]', this);
      if (!this.input) return;
      this.input.addEventListener('input', () => { this.clearError(); this.validate(false); if (this.dataset.googleKey) this.autocomplete(); });
      this.input.addEventListener('blur', () => setTimeout(() => this.hideSuggestions(), 150));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      // Validation au submit du formulaire produit
      const form = document.getElementById(this.dataset.form);
      form?.addEventListener('submit', (e) => {
        if (!this.validate(true)) { e.preventDefault(); e.stopImmediatePropagation(); this.input.focus(); this.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      }, true);
    }
    get value() { return this.input.value.trim(); }
    validate(showErrors) {
      const v = this.value;
      const pattern = this.input.dataset.setupPattern;
      let msg = '';
      if (!v) { if (this.dataset.required === 'true') msg = RT.strings.setupRequired; }
      else if (pattern === 'handle') {
        const clean = v.replace(/^@/, '').replace(/^https?:\/\/(www\.)?(instagram|tiktok|snapchat)\.com\/(@)?/i, '').replace(/\/.*$/, '');
        if (clean !== v) this.input.value = clean;
        if (!/^[a-zA-Z0-9._-]{1,60}$/.test(clean)) msg = RT.strings.setupInvalidHandle;
      } else if (pattern === 'facebook') {
        if (!/^https?:\/\/(www\.|m\.)?(facebook\.com|fb\.com|fb\.me)\/.+/i.test(v)) msg = RT.strings.setupInvalidFacebook;
      }
      const ok = !msg;
      this.classList.toggle('is-valid', ok && !!v);
      if (showErrors && msg) this.showError(msg);
      return ok;
    }
    showError(msg) { this.error.textContent = msg; this.error.hidden = false; this.classList.add('has-error'); this.input.setAttribute('aria-invalid', 'true'); }
    clearError() { this.error.hidden = true; this.classList.remove('has-error'); this.input.removeAttribute('aria-invalid'); }

    /* --- Google Places via le SDK Maps JavaScript (même mécanique que l'ancien thème) --- */
    loadGoogle() {
      if (this.googleReady) return this.googleReady;
      const key = this.dataset.googleKey;
      if (!(window.google && window.google.maps && window.google.maps.importLibrary)) {
        // Bootstrap officiel Google Maps (chargement dynamique, une seule fois)
        ((g) => { let h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window; b = b[c] || (b[c] = {}); const d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(), u = () => h || (h = new Promise(async (f, n) => { await (a = m.createElement('script')); e.set('libraries', [...r] + ''); for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]); e.set('callback', c + '.maps.' + q); a.src = `https://maps.${c}apis.com/maps/api/js?` + e; d[q] = f; a.onerror = () => (h = n(Error(p + ' could not load.'))); a.nonce = m.querySelector('script[nonce]')?.nonce || ''; m.head.append(a); })); d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))); })({ key, v: 'weekly', language: 'fr', region: 'FR' });
      }
      this.googleReady = window.google.maps.importLibrary('places').then((places) => { this.places = places; this.sessionToken = new places.AutocompleteSessionToken(); return places; });
      return this.googleReady;
    }
    autocomplete() {
      clearTimeout(this.timer);
      const q = this.value;
      if (this.placeId) { this.placeId.value = ''; this.reviewLink.value = ''; }
      if (q.length < 3) { this.hideSuggestions(); return; }
      this.timer = setTimeout(async () => {
        try {
          const places = await this.loadGoogle();
          const res = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: q, sessionToken: this.sessionToken, language: 'fr', region: 'fr' });
          if (q !== this.value) return;
          const items = (res.suggestions || []).map((s) => s.placePrediction).filter(Boolean).slice(0, 6).map((p) => ({
            placeId: p.placeId,
            main: p.mainText ? p.mainText.text : (p.text ? p.text.text : ''),
            secondary: p.secondaryText ? p.secondaryText.text : ''
          }));
          this.renderSuggestions(items);
        } catch (e) {
          // Clé refusée (référent, API non activée…) : repli en champ texte simple, sans bloquer l'achat
          console.warn('[RevTap] Google Places :', e?.message || e);
          delete this.dataset.googleKey;
          this.hideSuggestions();
        }
      }, 250);
    }
    renderSuggestions(items) {
      if (!this.suggestions) return;
      if (!items.length) { this.suggestions.innerHTML = `<li class="setup__suggestion setup__suggestion--empty">${RT.strings.setupNoResults}</li>`; this.suggestions.hidden = false; return; }
      this.suggestions.innerHTML = items.map((p, i) => `
        <li class="setup__suggestion" role="option" data-index="${i}" data-place-id="${p.placeId}" data-main="${p.main.replace(/"/g, '&quot;')}" data-secondary="${p.secondary.replace(/"/g, '&quot;')}">
          <span class="setup__suggestion-pin"><svg class="icon icon--sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg></span>
          <span class="setup__suggestion-text"><strong>${p.main}</strong><span>${p.secondary}</span></span>
        </li>`).join('');
      this.suggestions.hidden = false;
      this.activeIndex = -1;
      $$('.setup__suggestion[data-place-id]', this.suggestions).forEach((li) => li.addEventListener('mousedown', (e) => { e.preventDefault(); this.pick(li); }));
    }
    pick(li) {
      this.input.value = `${li.dataset.main}${li.dataset.secondary ? ' — ' + li.dataset.secondary : ''}`;
      if (this.placeId) { this.placeId.value = li.dataset.placeId; this.reviewLink.value = `https://search.google.com/local/writereview?placeid=${li.dataset.placeId}`; }
      this.sessionToken = this.places ? new this.places.AutocompleteSessionToken() : null;
      this.hideSuggestions();
      this.clearError();
      this.classList.add('is-valid');
    }
    hideSuggestions() { if (this.suggestions) { this.suggestions.hidden = true; } }
    onKey(e) {
      if (!this.suggestions || this.suggestions.hidden) return;
      const items = $$('.setup__suggestion[data-place-id]', this.suggestions);
      if (!items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((li, i) => li.classList.toggle('is-active', i === this.activeIndex));
      } else if (e.key === 'Enter' && this.activeIndex >= 0) { e.preventDefault(); this.pick(items[this.activeIndex]); }
      else if (e.key === 'Escape') this.hideSuggestions();
    }
  }
  customElements.define('rt-platform-setup', RTPlatformSetup);

  /* ---------- Compte à rebours expédition (heure de Paris, honnête) ---------- */
  class RTShippingCountdown extends HTMLElement {
    connectedCallback() {
      this.cutoff = parseInt(this.dataset.cutoff, 10) || 14;
      this.weekdaysOnly = this.dataset.weekdaysOnly === 'true';
      this.textEl = $('[data-countdown-text]', this);
      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    parisNow() {
      const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: 'numeric', minute: 'numeric', weekday: 'short', hour12: false }).formatToParts(new Date());
      const get = (t) => parts.find((p) => p.type === t)?.value;
      return { h: parseInt(get('hour'), 10), m: parseInt(get('minute'), 10), wd: get('weekday') };
    }
    tick() {
      const { h, m, wd } = this.parisNow();
      const isWeekend = this.weekdaysOnly && (wd.startsWith('sam') || wd.startsWith('dim'));
      const minutesLeft = this.cutoff * 60 - (h * 60 + m);
      const days = RT.strings?.days || { today: "aujourd'hui", tomorrow: 'demain', monday: 'lundi' };
      if (!isWeekend && minutesLeft > 0) {
        const hh = Math.floor(minutesLeft / 60); const mm = minutesLeft % 60;
        const timer = hh > 0 ? `${hh} h ${String(mm).padStart(2, '0')} min` : `${mm} min`;
        this.textEl.textContent = this.dataset.textToday.replace('[timer]', timer);
        this.classList.remove('is-later');
      } else {
        let day = days.tomorrow;
        if (this.weekdaysOnly && (wd.startsWith('ven') || wd.startsWith('sam') || wd.startsWith('dim'))) day = days.monday;
        this.textEl.textContent = this.dataset.textLater.replace('[day]', day);
        this.classList.add('is-later');
      }
    }
  }
  customElements.define('rt-shipping-countdown', RTShippingCountdown);

  /* ---------- Slider horizontal (scroll-snap + flèches) ---------- */
  class RTSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-slider-track]', this);
      $('[data-slider-prev]', this)?.addEventListener('click', () => this.scroll(-1));
      $('[data-slider-next]', this)?.addEventListener('click', () => this.scroll(1));
    }
    scroll(dir) {
      const item = this.track?.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({ left: dir * (item.offsetWidth + gap), behavior: 'smooth' });
    }
  }
  customElements.define('rt-slider', RTSlider);

  /* ---------- Témoignages vidéo : lecture au toucher (avec le son), une seule vidéo à la fois ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-testimonial-play]');
    if (!btn) return;
    const media = btn.closest('[data-testimonial-video]');
    const video = $('video', media);
    if (!video) return;
    if (video.paused) {
      $$('[data-testimonial-video].is-playing video').forEach((v) => v.pause());
      video.play().catch(() => {});
    } else video.pause();
  });
  ['play', 'pause'].forEach((type) => document.addEventListener(type, (e) => {
    e.target.closest?.('[data-testimonial-video]')?.classList.toggle('is-playing', type === 'play');
  }, true));

  /* ---------- Recherche prédictive ---------- */
  class RTPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-search-input]', this);
      this.results = $('[data-search-results]', this);
      this.timer = null;
      this.input?.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 250);
      });
      // Focus automatique à l'ouverture du drawer
      document.addEventListener('rt:drawer:open', (e) => {
        if (e.detail.id === 'SearchDrawer') setTimeout(() => this.input?.focus(), 120);
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      this.results.classList.add('is-loading');
      try {
        const url = `${this.dataset.url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = html.querySelector('#predictive-search-results');
        if (q === this.input.value.trim()) this.results.innerHTML = fresh ? fresh.outerHTML : '';
      } catch (e) { /* silencieux */ }
      this.results.classList.remove('is-loading');
    }
  }
  customElements.define('rt-predictive-search', RTPredictiveSearch);

  /* ---------- Produits recommandés (Section Rendering API) ---------- */
  class RTRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = html.querySelector('rt-recommendations');
          if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; this.classList.remove('is-pending'); initReveal(); }
          else this.remove();
        } catch (e) { this.remove(); }
        this.dataset.loaded = 'true';
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '400px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('rt-recommendations', RTRecommendations);

  /* ---------- Panier : produits complémentaires (tiroir et page) ---------- */
  class RTCartRecos extends HTMLElement {
    async connectedCallback() {
      if (!this.dataset.url) return;
      // Page panier : après un ajout, on recharge pour mettre à jour la liste des articles
      if (this.dataset.reload === 'true') {
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-quick-add]')) document.addEventListener('rt:cart:updated', () => window.location.reload(), { once: true });
        });
      }
      try {
        const res = await fetch(this.dataset.url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = html.querySelector('[data-cart-recos]');
        if (fresh && this.isConnected) this.replaceChildren(fresh);
      } catch (e) { /* pas de recommandations : le bloc reste vide et invisible */ }
    }
  }
  customElements.define('rt-cart-recos', RTCartRecos);

  /* ---------- Composer son pack : N produits au choix, ajoutés au panier en une fois ---------- */
  class RTBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.size = parseInt(this.dataset.size, 10) || 5;
      // Paliers [quantité, prix total en centimes], du plus petit au plus grand
      this.tiers = (this.dataset.tiers || '').split('|').filter(Boolean)
        .map((t) => t.split(':').map((n) => parseInt(n, 10))).sort((a, b) => a[0] - b[0]);
      this.items = $$('[data-bundle-item]', this);
      this.slots = $$('[data-bundle-slots] > li', this);
      this.submit = $('[data-bundle-submit]', this);
      this.picked = []; // éléments choisis, dans l'ordre d'ajout (un par exemplaire)

      this.addEventListener('click', (e) => {
        const add = e.target.closest('[data-bundle-add]');
        const remove = e.target.closest('[data-bundle-remove]');
        const slot = e.target.closest('[data-bundle-slot-remove]');
        if (add) this.add(add.closest('[data-bundle-item]'));
        else if (remove) this.remove(remove.closest('[data-bundle-item]'));
        else if (slot) { this.picked.splice(parseInt(slot.dataset.bundleSlotRemove, 10), 1); this.render(); }
        else if (e.target.closest('[data-bundle-submit]')) this.addToCart();
      });
      this.render();
    }
    get full() { return this.picked.length >= this.size; }
    /* Prix pour n produits : prix unitaire du dernier palier atteint × n
       (avant le premier palier, c'est son prix unitaire qui s'applique : "5:79" → 15,80 € la plaque) */
    priceFor(n) {
      const tier = this.tiers.filter(([qty]) => qty <= n).pop() || this.tiers[0];
      return tier ? Math.round((tier[1] / tier[0]) * n) : 0;
    }
    add(item) {
      if (!item || this.full) return;
      this.picked.push(item);
      this.render();
    }
    remove(item) {
      const i = this.picked.lastIndexOf(item);
      if (i > -1) { this.picked.splice(i, 1); this.render(); }
    }
    render() {
      const count = this.picked.length;
      const left = this.size - count;
      this.classList.toggle('is-full', this.full);

      this.items.forEach((item) => {
        const qty = this.picked.filter((p) => p === item).length;
        item.classList.toggle('is-selected', qty > 0);
        $('[data-bundle-qty]', item).textContent = qty;
        $('[data-bundle-stepper]', item).hidden = qty === 0;
        $('.bundle-item__add', item).hidden = qty > 0;
        const badge = $('[data-bundle-count]', item);
        badge.hidden = qty === 0;
        badge.textContent = `×${qty}`;
        $$('[data-bundle-add]', item).forEach((b) => { b.disabled = this.full; });
      });

      this.slots.forEach((slot, i) => {
        const item = this.picked[i];
        slot.classList.toggle('is-filled', !!item);
        slot.innerHTML = item
          ? `<button type="button" data-bundle-slot-remove="${i}" aria-label="${RT.strings?.remove || 'Retirer'} : ${item.dataset.title}">${item.dataset.image ? `<img src="${item.dataset.image}" alt="" width="80" height="80">` : ''}<span class="bundle__slot-x" aria-hidden="true">×</span></button>`
          : `<span>${i + 1}</span>`;
      });

      $('[data-bundle-progress]', this).textContent = `${count}/${this.size}`;
      const label = $('[data-bundle-submit-label]', this);
      if (left > 0) label.textContent = left === 1 ? this.dataset.labelMoreOne : this.dataset.labelMoreMany.replace('[n]', left);
      else label.textContent = this.dataset.labelAdded;
      this.submit.disabled = left > 0;

      // Prix selon le nombre de produits choisis ; prix barré et économie calculés sur ces produits
      const price = this.priceFor(count);
      const full = this.picked.reduce((sum, p) => sum + (parseInt(p.dataset.compare, 10) || 0), 0);
      const showSaving = count > 0 && full > price;
      $('[data-bundle-units]', this).textContent = `${count} ${count > 1 ? this.dataset.unitMany : this.dataset.unitOne}`;
      $('[data-bundle-price]', this).textContent = formatMoney(price);
      const cmp = $('[data-bundle-compare]', this);
      const save = $('[data-bundle-save]', this);
      cmp.hidden = save.hidden = !showSaving;
      if (showSaving) {
        cmp.textContent = formatMoney(full);
        save.textContent = this.dataset.labelSave.replace('[amount]', formatMoney(full - price));
      }
    }
    async addToCart() {
      if (!this.full || this.submit.classList.contains('is-loading')) return;
      // Regroupe les exemplaires par produit : une ligne de panier par plateforme
      const lines = new Map();
      this.picked.forEach((item) => lines.set(item, (lines.get(item) || 0) + 1));
      const items = Array.from(lines, ([item, quantity]) => {
        const properties = {};
        if (item.dataset.platform) properties._plateforme = item.dataset.platform;
        return { id: item.dataset.id, quantity, properties };
      });
      this.submit.classList.add('is-loading');
      try {
        await Cart.add(items);
        this.picked = [];
        this.render();
        $('#CartDrawer') ? $('#CartDrawer').open() : (window.location.href = RT.routes.cart);
      } catch (err) { toast(err.message, 'error'); }
      finally { this.submit.classList.remove('is-loading'); }
    }
  }
  customElements.define('rt-bundle-builder', RTBundleBuilder);

  /* ---------- Galerie produit : vignette active suivant le scroll ---------- */
  // ramène la vignette en vue en ne faisant défiler QUE la bande de vignettes
  // (scrollIntoView ferait aussi défiler la page et remonterait le visiteur)
  const revealThumb = (thumb) => {
    const box = thumb?.parentElement;
    if (!box) return;
    const b = box.getBoundingClientRect();
    const t = thumb.getBoundingClientRect();
    let left = box.scrollLeft; let top = box.scrollTop;
    if (t.left < b.left) left -= b.left - t.left; else if (t.right > b.right) left += t.right - b.right;
    if (t.top < b.top) top -= b.top - t.top; else if (t.bottom > b.bottom) top += t.bottom - b.bottom;
    if (left !== box.scrollLeft || top !== box.scrollTop) box.scrollTo({ left, top, behavior: 'smooth' });
  };
  const initGallerySync = () => {
    const track = $('.product__media-scroller');
    if (!track || track.dataset.synced) return;
    track.dataset.synced = 'true';
    let pending = null;
    track.addEventListener('scroll', () => {
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        const items = $$('[data-media-id]', track);
        const center = track.scrollLeft + track.clientWidth / 2;
        let best = null; let bestDist = Infinity;
        items.forEach((it) => { const d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - center); if (d < bestDist) { bestDist = d; best = it; } });
        if (!best) return;
        $$('[data-thumb]').forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === best.dataset.mediaId));
        $$('[data-gallery-dot]').forEach((d) => d.classList.toggle('is-active', d.dataset.galleryDot === best.dataset.mediaId));
        revealThumb($(`[data-thumb="${best.dataset.mediaId}"]`));
      }, 80);
    }, { passive: true });
  };
  document.addEventListener('DOMContentLoaded', initGallerySync);
  document.addEventListener('shopify:section:load', initGallerySync);

  /* ---------- Galerie produit : la vidéo affichée se lance seule, en boucle et sans son ---------- */
  const initGalleryVideos = () => {
    const track = $('.product__media-scroller');
    if (!track || track.dataset.autoplay) return;
    const videos = $$('.product__media--player video', track);
    if (!videos.length) return;
    track.dataset.autoplay = 'true';
    videos.forEach((v) => { v.muted = true; v.loop = true; v.playsInline = true; });
    if (!('IntersectionObserver' in window)) { videos.forEach((v) => v.play().catch(() => {})); return; }
    // root = le carrousel : une vidéo ne démarre qu'une fois la diapo amenée à l'écran
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting) restartVideo(v);  // on arrive sur la diapo : la vidéo repart du début
        else v.pause();
      });
    }, { root: track, threshold: 0.6 });
    videos.forEach((v) => io.observe(v));
  };
  document.addEventListener('DOMContentLoaded', initGalleryVideos);
  document.addEventListener('shopify:section:load', initGalleryVideos);

  // vignette ou point de pagination : la vidéo visée recommence au début
  const restartVideo = (v) => {
    if (!v) return;
    v.muted = true;
    try { v.currentTime = 0; } catch (err) { /* métadonnées pas encore chargées */ }
    v.play().catch(() => {});
  };
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-thumb], [data-gallery-dot]');
    if (!el) return;
    const id = el.dataset.thumb || el.dataset.galleryDot;
    restartVideo($(`.product__media[data-media-id="${id}"] video`));
  });

  /* ---------- Galerie produit : vignette vidéo figée sur l'image à 1 s de la fin ---------- */
  const initThumbFrames = () => {
    $$('.product__thumb video').forEach((v) => {
      if (v.dataset.frame) return;
      v.dataset.frame = 'true';
      v.autoplay = false; v.loop = false; v.controls = false; v.muted = true;
      v.pause();
      const seek = () => {
        if (!isFinite(v.duration) || v.duration <= 0) return;
        v.currentTime = Math.max(0, v.duration - 1);
      };
      v.addEventListener('seeked', () => v.pause(), { once: true });
      if (v.readyState >= 1) seek(); else v.addEventListener('loadedmetadata', seek, { once: true });
    });
  };
  document.addEventListener('DOMContentLoaded', initThumbFrames);
  document.addEventListener('shopify:section:load', initThumbFrames);

  /* ---------- Galerie produit : 5 s par photo, une vidéo passe la main quand elle est finie ---------- */
  const GALLERY_DELAY = 5000;
  const initGalleryRotation = () => {
    const track = $('.product__media-scroller');
    if (!track || track.dataset.rotating) return;
    const slides = $$('[data-media-id]', track);
    if (slides.length < 2) return;
    track.dataset.rotating = 'true';

    const dotsBox = $('[data-gallery-dots]');
    const dots = $$('[data-gallery-dot]', dotsBox);
    const origin = slides[0].offsetLeft; // les diapos peuvent être décalées par un ancêtre positionné
    const current = () => {
      let best = 0; let bestDist = Infinity;
      slides.forEach((sl, i) => {
        const d = Math.abs((sl.offsetLeft - origin) - track.scrollLeft);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    };
    const go = (i) => track.scrollTo({ left: slides[i].offsetLeft - origin, behavior: 'smooth' });
    const next = () => go((current() + 1) % slides.length);
    const slideVideo = (i) => $('video', slides[i]);

    // une vidéo tient l'affiche jusqu'à sa fin : pas de boucle, c'est elle qui enchaîne
    slides.forEach((sl, i) => {
      const v = slideVideo(i);
      if (!v) return;
      v.loop = false;
      v.addEventListener('ended', () => {
        if (paused || document.hidden) { restartVideo(v); return; } // en pause : la vidéo reboucle sur place
        next();
      });
    });

    // clic sur un point : on affiche ce média et on repart sur 5 s pleines
    dots.forEach((dot, i) => dot.addEventListener('click', () => { go(i); reset(); }));
    $$('[data-thumb]').forEach((t) => t.addEventListener('click', reset));

    const auto = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let elapsed = 0; let last = null; let raf = null; let paused = false;
    const progress = (v) => dotsBox?.style.setProperty('--gallery-progress', v.toFixed(3));
    function reset() { elapsed = 0; last = null; progress(0); }
    const tick = (now) => {
      if (last === null) last = now;
      const dt = Math.min(now - last, 100); // onglet en arrière-plan : pas de saut de diapo
      last = now;
      const v = slideVideo(current());
      if (v && !v.ended && !(v.paused && !paused)) {
        // diapo vidéo en lecture : le point se remplit au rythme de la vidéo, pas du minuteur
        elapsed = 0;
        progress(v.duration > 0 && isFinite(v.duration) ? v.currentTime / v.duration : 0);
      } else {
        // photo, vidéo terminée, ou lecture refusée par le navigateur : minuteur de 5 s
        if (!paused && !document.hidden) {
          elapsed += dt;
          if (elapsed >= GALLERY_DELAY) { elapsed = 0; next(); }
        }
        progress(elapsed / GALLERY_DELAY);
      }
      raf = requestAnimationFrame(tick);
    };
    const start = () => { if (!auto || raf) return; last = null; raf = requestAnimationFrame(tick); };
    const stop = () => { cancelAnimationFrame(raf); raf = null; };

    // on suspend au survol / au doigt, on reprend ensuite
    const hold = () => { paused = true; };
    const release = () => { paused = false; last = null; };
    track.addEventListener('pointerenter', hold);
    track.addEventListener('pointerleave', release);
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', release, { passive: true });
    track.addEventListener('focusin', hold);
    track.addEventListener('focusout', release);
    document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });

    // pas de rotation tant que la galerie n'est pas à l'écran
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((en) => { en[0].isIntersecting ? start() : stop(); }, { threshold: 0.3 }).observe(track);
    } else start();
  };
  document.addEventListener('DOMContentLoaded', initGalleryRotation);
  document.addEventListener('shopify:section:load', initGalleryRotation);

  /* ---------- Galerie produit : vignettes ---------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    e.preventDefault();
    $$('[data-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
    // défilement horizontal du carrousel uniquement, la page ne bouge pas
    const slide = document.querySelector(`.product__media[data-media-id="${thumb.dataset.thumb}"]`);
    const track = slide?.closest('.product__media-scroller');
    if (track) track.scrollTo({ left: track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left, behavior: 'smooth' });
  });

  /* ---------- Reveal on scroll ---------- */
  const initReveal = () => {
    const items = $$('.reveal');
    if (!items.length || !('IntersectionObserver' in window)) { items.forEach((i) => i.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    items.forEach((i) => io.observe(i));
  };

  /* ---------- Compteur animé (stats) ---------- */
  const initCounters = () => {
    const els = $$('[data-count-to]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const el = en.target; io.unobserve(el);
        const to = parseFloat(el.dataset.countTo); const suffix = el.dataset.countSuffix || '';
        const dur = 1200; const start = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - start) / dur); const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(to * eased).toLocaleString('fr-FR') + suffix;
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.5 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Accordéon : un seul ouvert (option) ---------- */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.closest('[data-accordion-single]');
    if (!group) return;
    $$('details[open]', group).forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  /* ---------- Bannière cookies (API Customer Privacy de Shopify) ---------- */
  class RTCookieBanner extends HTMLElement {
    connectedCallback() {
      this.prefs = $('[data-cookie-prefs]', this);
      $$('[data-cookie-accept]', this).forEach((b) => b.addEventListener('click', () => this.consent(true)));
      $('[data-cookie-decline]', this)?.addEventListener('click', () => this.consent(false));
      $('[data-cookie-manage]', this)?.addEventListener('click', () => this.openPrefs());
      $$('[data-cookie-prefs-close]', this).forEach((b) => b.addEventListener('click', () => this.closePrefs()));
      $('[data-cookie-save]', this)?.addEventListener('click', () => this.save());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.prefs.hidden) this.closePrefs(); });

      if (this.dataset.preview === 'true') { this.show(); return; }
      const init = () => {
        const api = window.Shopify?.customerPrivacy;
        if (!api) return;
        // Décision déjà prise ? (marqueur du thème, ou consentement déjà présent côté Shopify).
        // shouldShowBanner() seul n'est pas fiable : il peut rester true après un consentement enregistré.
        let local = null; try { local = JSON.parse(localStorage.getItem('rt_consent') || 'null'); } catch (e) { /* stockage indisponible */ }
        const hasCookie = /(?:^|;\s*)rt_consent=1/.test(document.cookie);
        const current = api.currentVisitorConsent?.() || {};
        const decided = !!local || hasCookie || ['analytics', 'marketing', 'preferences'].some((k) => current[k] === 'yes' || current[k] === 'no');
        if (decided) {
          if (local && api.shouldShowBanner()) api.setTrackingConsent(local, () => {});
        } else if (api.shouldShowBanner()) {
          this.show();
        }
        // Pré-remplir les toggles avec un consentement déjà exprimé (sinon tout coché)
        if (!api.shouldShowBanner()) {
          const current = api.currentVisitorConsent?.() || {};
          $$('[data-cookie-cat]', this).forEach((i) => { if (current[i.dataset.cookieCat] === 'no') i.checked = false; });
        }
      };
      // L'API peut arriver après theme.js (scripts Shopify différés) : on attend jusqu'à 10 s.
      let tries = 0; let requested = false;
      const wait = () => {
        if (window.Shopify?.customerPrivacy) { init(); return; }
        if (!requested && window.Shopify?.loadFeatures) {
          requested = true;
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => { if (!err) init(); });
          return;
        }
        if (tries++ < 50) setTimeout(wait, 200);
      };
      wait();
    }
    show() { this.hidden = false; setTimeout(() => this.classList.add('is-visible'), 30); }
    hide() { this.classList.remove('is-visible'); setTimeout(() => { this.hidden = true; }, 350); }
    openPrefs() { this.prefs.hidden = false; document.body.classList.add('is-locked'); setTimeout(() => $('[data-cookie-prefs-close]', this)?.focus(), 60); }
    closePrefs() { this.prefs.hidden = true; document.body.classList.remove('is-locked'); }
    consent(all) {
      this.apply({ analytics: all, marketing: all, preferences: all });
    }
    save() {
      const c = {};
      $$('[data-cookie-cat]', this).forEach((i) => { c[i.dataset.cookieCat] = i.checked; });
      this.apply(c);
    }
    apply(c) {
      const payload = { analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, sale_of_data: !!c.marketing };
      const done = () => {
        // Mémoriser la décision côté thème (localStorage + cookie 1 an) pour ne plus réafficher la bannière
        try { localStorage.setItem('rt_consent', JSON.stringify(payload)); } catch (e) { /* ignore */ }
        try { document.cookie = `rt_consent=1; max-age=31536000; path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; } catch (e) { /* ignore */ }
        this.closePrefs(); this.hide(); publish('rt:consent', payload);
      };
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) window.Shopify.customerPrivacy.setTrackingConsent(payload, done);
      else done(new Error('no api'));
    }
  }
  customElements.define('rt-cookie-banner', RTCookieBanner);
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cookie-open]');
    if (!t) return;
    e.preventDefault();
    const b = $('rt-cookie-banner');
    if (b) { b.show(); b.openPrefs(); }
  });

  /* ---------- Barre d'annonce : une annonce à la fois ---------- */
  class RTAnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 3000;
      $('[data-slide-prev]', this)?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
      $('[data-slide-next]', this)?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.restart());
      this.restart();
    }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.index));
    }
    restart() {
      clearInterval(this.timer);
      if (this.dataset.autoplay === 'true') this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }
  }
  customElements.define('rt-announcement-slider', RTAnnouncementSlider);

  /* ---------- Étapes en vidéo : une étape active à la fois, défilement auto ---------- */
  class RTVideoSteps extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-steps-track]', this);
      this.steps = $$('[data-step]', this);
      this.dots = $$('[data-step-dot]', this);
      if (!this.steps.length) return;
      this.duration = parseInt(this.dataset.duration, 10) || 5000;
      this.auto = this.steps.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = 0;
      this.elapsed = 0;
      this.visible = false;

      this.steps.forEach((step, i) => {
        step.addEventListener('click', () => this.go(i));
        // Fin de la vidéo d'une étape : on passe à la suivante (si le défilement auto est actif, sinon la vidéo reboucle)
        const video = $('video', step);
        if (!video) return;
        video.loop = !this.auto;
        video.addEventListener('ended', () => {
          if (i !== this.index || !this.visible) return;
          if (this.auto) this.go(i + 1);
          else { video.currentTime = 0; video.play().catch(() => {}); }
        });
      });
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      // Swipe horizontal : étape suivante / précédente
      let startX = null;
      this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // Éditeur de thème : affiche l'étape sélectionnée et fige le défilement (les vidéos rebouclent)
      this.addEventListener('shopify:block:select', (e) => {
        this.auto = false;
        $$('video', this).forEach((v) => { v.loop = true; });
        const i = this.steps.indexOf(e.target);
        if (i > -1) this.go(i);
      });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((en) => {
          this.visible = en[0].isIntersecting;
          if (this.visible) {
            this.play();
            if (this.steps[1]) { const next = $('video', this.steps[1]); if (next) this.preload(next); }
          } else this.pause();
        }, { threshold: 0.25 });
        this.io.observe(this);
      } else { this.visible = true; this.play(); }
    }
    disconnectedCallback() {
      this.io?.disconnect();
      cancelAnimationFrame(this.raf);
    }
    go(i) {
      const n = this.steps.length;
      this.index = ((i % n) + n) % n;
      this.elapsed = 0;
      this.style.setProperty('--vstep-progress', 0);
      this.steps.forEach((step, k) => {
        // position autour de l'étape active (au centre) : suivante à droite, précédente à gauche
        const offset = (k - this.index + n) % n;
        const active = offset === 0;
        step.classList.toggle('is-active', active);
        step.classList.toggle('is-next', offset === 1);
        step.classList.toggle('is-prev', offset > 1 && offset === n - 1);
        $('[data-step-btn]', step)?.setAttribute('aria-current', active ? 'step' : 'false');
        const video = $('video', step);
        if (!video) return;
        if (active) { if (video.currentTime) video.currentTime = 0; if (this.visible) video.play().catch(() => {}); }
        else video.pause();
        // la carte suivante se précharge pendant que l'active joue : pas de trou au changement
        if (offset === 1) this.preload(video);
      });
      this.dots.forEach((dot, k) => dot.classList.toggle('is-active', k === this.index));
    }
    preload(video) {
      if (video.dataset.preloaded) return;
      video.dataset.preloaded = '1';
      video.preload = 'auto';
      video.load();
    }
    play() {
      $('video', this.steps[this.index])?.play().catch(() => {});
      cancelAnimationFrame(this.raf);
      if (!this.auto) return;
      let last = performance.now();
      const tick = (now) => {
        // delta plafonné : un onglet en arrière-plan ne fait pas sauter d'étape
        const dt = Math.min(now - last, 100);
        last = now;
        const video = $('video', this.steps[this.index]);
        if (video && !video.paused && video.duration > 0 && isFinite(video.duration)) {
          // étape vidéo : la barre suit la lecture, l'étape suivante arrive sur l'évènement « ended »
          this.style.setProperty('--vstep-progress', (video.currentTime / video.duration).toFixed(3));
        } else {
          // étape sans vidéo (ou vidéo bloquée) : minuterie classique
          this.elapsed += dt;
          if (this.elapsed >= this.duration) this.go(this.index + 1);
          else this.style.setProperty('--vstep-progress', (this.elapsed / this.duration).toFixed(3));
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
    pause() {
      cancelAnimationFrame(this.raf);
      $('video', this.steps[this.index])?.pause();
    }
  }
  customElements.define('rt-video-steps', RTVideoSteps);

  /* ---------- Défilement horizontal automatique (mobile uniquement) ---------- */
  class RTAutoScroller extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-auto-track]', this);
      if (!this.track || this.track.children.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.visible = !('IntersectionObserver' in window);
      this.paused = false;
      // Le visiteur garde la main : pause pendant qu'il fait défiler, reprise un peu après
      this.track.addEventListener('touchstart', () => { this.paused = true; clearTimeout(this.resume); }, { passive: true });
      this.track.addEventListener('touchend', () => { this.resume = setTimeout(() => { this.paused = false; }, 4000); }, { passive: true });
      if (!this.visible) {
        this.io = new IntersectionObserver((en) => { this.visible = en[0].isIntersecting; }, { threshold: 0.4 });
        this.io.observe(this);
      }
      this.timer = setInterval(() => this.next(), parseInt(this.dataset.interval, 10) || 3000);
    }
    disconnectedCallback() {
      this.io?.disconnect();
      clearInterval(this.timer);
      clearTimeout(this.resume);
    }
    next() {
      if (!this.mobile.matches || !this.visible || this.paused || document.hidden) return;
      const t = this.track;
      const atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 4;
      const gap = parseFloat(getComputedStyle(t).gap) || 0;
      t.scrollTo({ left: atEnd ? 0 : t.scrollLeft + t.firstElementChild.offsetWidth + gap, behavior: 'smooth' });
    }
  }
  customElements.define('rt-auto-scroller', RTAutoScroller);

  /* ---------- Mises en situation (onglets) : une photo à la fois, défilement auto ---------- */
  class RTSpots extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[data-spot-tab]', this);
      this.slides = $$('[data-spot-slide]', this);
      this.nav = $('[data-spot-nav]', this);
      this.stage = $('[data-spot-stage]', this);
      if (this.slides.length < 2) return;
      this.duration = parseInt(this.dataset.interval, 10) || 4000;
      this.auto = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = 0;
      this.elapsed = 0;

      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.go(i)));
      this.addEventListener('shopify:block:select', (e) => { const i = this.tabs.indexOf(e.target); if (i > -1) { this.auto = false; this.go(i); } });

      // Swipe horizontal sur la photo
      let startX = null;
      this.stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.stage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      if (!this.auto) return;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((en) => { if (en[0].isIntersecting) this.play(); else cancelAnimationFrame(this.raf); }, { threshold: 0.3 });
        this.io.observe(this);
      } else this.play();
    }
    disconnectedCallback() {
      this.io?.disconnect();
      cancelAnimationFrame(this.raf);
    }
    go(i) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.elapsed = 0;
      this.style.setProperty('--spot-progress', 0);
      this.slides.forEach((slide, k) => {
        // position autour de la photo active : suivante à droite, précédente à gauche (carrousel mobile)
        const offset = (k - this.index + n) % n;
        slide.classList.toggle('is-active', offset === 0);
        slide.classList.toggle('is-next', offset === 1);
        slide.classList.toggle('is-prev', offset > 1 && offset === n - 1);
      });
      this.tabs.forEach((tab, k) => {
        tab.classList.toggle('is-active', k === this.index);
        tab.setAttribute('aria-selected', k === this.index ? 'true' : 'false');
      });
      // Mobile : garde la pastille active visible, sans faire bouger la page
      const tab = this.tabs[this.index];
      if (tab && this.nav.scrollWidth > this.nav.clientWidth) {
        this.nav.scrollTo({ left: tab.offsetLeft - this.nav.offsetLeft - (this.nav.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
      }
    }
    play() {
      cancelAnimationFrame(this.raf);
      let last = performance.now();
      const tick = (now) => {
        this.elapsed += Math.min(now - last, 100);
        last = now;
        if (this.elapsed >= this.duration) this.go(this.index + 1);
        else this.style.setProperty('--spot-progress', (this.elapsed / this.duration).toFixed(3));
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }
  customElements.define('rt-spots', RTSpots);

  /* ---------- Compte à rebours d'offre (barre dédiée) ---------- */
  const initOfferCountdown = () => {
    const els = $$('[data-offer-countdown]');
    if (!els.length) return;
    const minutes = parseInt(els[0].dataset.minutes, 10) || 10;
    const ended = els[0].dataset.ended || 'restart';
    const KEY = 'rt_offer_end';
    const now = () => Date.now();
    let end = 0;
    try { end = parseInt(localStorage.getItem(KEY), 10) || 0; } catch (e) { /* stockage indisponible */ }
    const start = () => { end = now() + minutes * 60000; try { localStorage.setItem(KEY, String(end)); } catch (e) { /* ignore */ } };
    if (!end || end - now() > minutes * 60000) start();
    const tick = () => {
      let left = end - now();
      if (left <= 0) {
        if (ended === 'hide') { els.forEach((el) => { (el.closest('.countdown-bar') || el).hidden = true; }); clearInterval(timer); return; }
        start(); left = end - now();
      }
      const m = Math.floor(left / 60000); const s = Math.floor((left % 60000) / 1000);
      const txt = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      const esc = (str) => str.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      els.forEach((el) => {
        const t = $('[data-offer-text]', el); if (!t) return;
        const tpl = el.dataset.text || '[countdown]';
        if (t.hasAttribute('data-offer-badge')) {
          const badge = $('.countdown-bar__badge', t);
          if (badge) badge.textContent = txt; // ne re-rend pas tout : évite le clignotement
          else t.innerHTML = esc(tpl).replace('[countdown]', `<span class="badge badge--accent countdown-bar__badge">${txt}</span>`);
        } else t.textContent = tpl.replace('[countdown]', txt);
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
  };

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initCounters();
    initOfferCountdown();
  });

  // Ré-init dans l'éditeur de thème
  document.addEventListener('shopify:section:load', () => { initReveal(); initCounters(); initOfferCountdown(); });

  window.RevTap.toast = toast;
  window.RevTap.formatMoney = formatMoney;
})();
